import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api, { API, formatErr } from "../lib/api";
import { toast } from "sonner";
import { Heart, ScanLine, ArrowRight } from "lucide-react";
import VenuefyMark from "../components/VenuefyMark";
import { T, useI18n } from "../lib/i18n";
import { LangToggle } from "../components/StaffShell";

const HERO = "https://images.unsplash.com/photo-1771450092341-a038d0aba751?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwyfHx0aGUlMjBsZWdhY3klMjBjYXN0bGUlMjB3ZWRkaW5nJTIwdmVudWV8ZW58MHx8fHwxNzc3ODE3NjA2fDA&ixlib=rb-4.1.0&q=85";

export default function Landing() {
  const navigate = useNavigate();
  const { user, joinGuest, logout } = useAuth();
  const [event, setEvent] = useState(null);
  const [fullName, setFullName] = useState("");
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState(null);
  const [showQR, setShowQR] = useState(false);
  useI18n(); // instant re-render on language switch

  useEffect(() => {
    api.get("/events/current").then(({ data }) => setEvent(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!user) return;
    const map = {
      guest: "/guest",
      kitchen: "/kitchen",
      coordinator: "/coordinator",
      couple: "/couple",
      admin: "/admin",
      dj: "/dj",
      photographer: "/photographer",
      embroidery: "/embroidery",
    };
    const dest = map[user.role];
    if (dest) navigate(dest, { replace: true });
  }, [user, navigate]);

  const submit = async (e, table_id = null) => {
    e?.preventDefault?.();
    if (!fullName.trim()) {
      toast.error(T("Please enter your full name"));
      return;
    }
    setLoading(true);
    try {
      const res = await joinGuest(fullName, table_id);
      if (res?.needs_disambiguation) {
        setOptions(res.options);
      } else {
        toast.success(T("Welcome!"));
      }
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative bg-motif">
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${HERO})`, filter: "saturate(0.95)" }}
      />
      <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.18) 0%, rgba(0,0,0,0.45) 60%, rgba(0,0,0,0.6) 100%)" }} />
      <div className="relative z-10 min-h-screen flex flex-col">
        <header className="px-5 sm:px-8 py-5 flex items-center justify-between text-white">
          <div className="flex items-center gap-2">
            <VenuefyMark size={32} left="#ffffff" right="var(--motif-primary, #c2a14d)" bars="var(--motif-primary, #c2a14d)" />
            <span className="font-display text-xl tracking-tight">Venuefy</span>
          </div>
          <div className="flex items-center gap-3">
            <LangToggle light />
            <Link to="/vendor" className="text-xs tracking-luxe font-semibold hover:opacity-70 transition" style={{ color: "#111111" }} data-testid="staff-link">
              {T("Vendor sign in")}
            </Link>
          </div>
        </header>

        <main className="flex-1 flex items-center justify-center px-5 py-10">
          <div className="w-full max-w-md">
            <div className="text-center text-white mb-6 fadein">
              <div className="text-[11px] tracking-luxe text-white/70 mb-3">{T("tonight at")}</div>
              <h1 className="font-display text-5xl sm:text-6xl leading-tight">
                {event ? event.venue : "The Legacy Castle"}
              </h1>
              <div className="mt-3 text-xs tracking-luxe text-white/85">
                {event ? `${event.bride} & ${event.groom} · ${formatDate(event.date)}` : "Aimee & Riyaz"}
              </div>
            </div>

            <div className="card-elev p-7 fadein" style={{ boxShadow: "0 30px 80px rgba(0,0,0,0.35)" }}>
              {!options ? (
                <form onSubmit={submit} className="space-y-4">
                  <div className="text-center mb-2">
                    <div className="divider-orn text-[10px] tracking-luxe">{T("join the night")}</div>
                  </div>
                  <h2 className="font-display text-2xl text-center text-motif">{T("Welcome, friend")}</h2>
                  <p className="text-center text-motif-muted text-sm -mt-2">{T("No app, no password — just your name.")}</p>

                  <input
                    className="input-motif"
                    placeholder={T("Full name (as on your invitation)")}
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    autoFocus
                    autoComplete="name"
                    data-testid="guest-full-name"
                  />
                  <button
                    type="submit"
                    disabled={loading}
                    className="btn-primary w-full flex items-center justify-center gap-2"
                    data-testid="guest-join-btn"
                  >
                    {loading ? T("Joining…") : T("Step inside")}
                    <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                  </button>

                  <div className="flex items-center justify-center gap-2 pt-2 text-motif-muted text-xs">
                    <Heart className="w-3.5 h-3.5" strokeWidth={1.5} />
                    <span>{T("Live food & drink, music, games, photos.")}</span>
                  </div>
                </form>
              ) : (
                <div className="space-y-3 fadein">
                  <h2 className="font-display text-2xl text-motif">{T("There is more than one of you here")}</h2>
                  <p className="text-motif-muted text-sm">{T("Pick your table to continue.")}</p>
                  {options.map((o) => (
                    <button
                      key={o.id}
                      onClick={(e) => submit(e, o.id)}
                      className="w-full text-left card-elev p-4 hover:bg-accent-motif transition"
                      data-testid={`guest-disambig-${o.id}`}
                    >
                      <div className="font-display text-lg text-motif">{T("Table")} {o.table_name || "—"}</div>
                      <div className="text-xs tracking-luxe text-motif-muted">{T("tap to join")}</div>
                    </button>
                  ))}
                  <button onClick={() => setOptions(null)} className="text-xs text-motif-muted underline">
                    {T("Back")}
                  </button>
                </div>
              )}
            </div>

            <div className="mt-6 text-center">
              <button
                onClick={() => setShowQR((v) => !v)}
                className="text-white/85 text-xs tracking-luxe inline-flex items-center gap-2"
                data-testid="show-qr-btn"
              >
                <ScanLine className="w-3.5 h-3.5" strokeWidth={1.5} />
                {showQR ? T("hide QR") : T("share this app · QR")}
              </button>
              {showQR && (
                <div className="mt-4 flex justify-center fadein">
                  <div className="card-elev p-3">
                    <img
                      src={`${API}/qr?url=${encodeURIComponent(`${window.location.origin}/riyazandaimee`)}`}
                      alt="Join QR"
                      className="w-44 h-44"
                      data-testid="join-qr"
                    />
                    <div className="text-[10px] tracking-luxe text-motif-muted text-center mt-2">{T("point a camera")}</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </main>

        <footer className="text-center text-white/60 text-[11px] tracking-luxe py-4">
          weddingOS · designed for one perfect night
        </footer>
      </div>
    </div>
  );
}

function formatDate(s) {
  if (!s) return "";
  try {
    const d = new Date(s + "T00:00:00");
    return d.toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });
  } catch {
    return s;
  }
}
